import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PDFDocument } from 'pdf-lib';
import type { ReportData } from './types';
import { htmlToBlocks } from './htmlToBlocks';
import type { Block, InlineRun } from './types';
import { API_BASE } from '../apiBase';
import { hyphenateSync } from 'hyphen/de';

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN_LEFT = 25;
const MARGIN_RIGHT = 25;
const MARGIN_TOP = 25;
const MARGIN_BOTTOM = 25;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;
const HEADER_HEIGHT = 10;
const FOOTER_HEIGHT = 10;
const USABLE_TOP = MARGIN_TOP + HEADER_HEIGHT;
const USABLE_BOTTOM = PAGE_HEIGHT - MARGIN_BOTTOM - FOOTER_HEIGHT;

function formatDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

function sanitizeFilename(s: string): string {
  return s.replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, '_').replace(/_+/g, '_');
}

/**
 * Ob der erste Block eines Abschnitts eine redundante Heading-Kopie des Abschnittstitels
 * ist und deshalb aus dem Fließtext entfernt werden soll (sonst erscheint die Überschrift
 * doppelt im PDF — einmal von exportPdf selbst gezeichnet, einmal aus dem gespeicherten
 * Text). Vergleich ignoriert die führende Nummer, weil Abschnitte umnummeriert werden
 * koennen (z.B. "Grundsätze" ausgeschlossen -> Confirmation rutscht von 7 auf 6), der im
 * Text gespeicherte Default-Heading-Block aber die urspruengliche Zahl behaelt.
 */
export function isRedundantHeadingBlock(headingText: string, sectionTitle: string): boolean {
  const stripLeadingNumber = (s: string) => s.replace(/^\d+\.\s*/, '').trim();
  return stripLeadingNumber(headingText.trim().toLowerCase()) === stripLeadingNumber(sectionTitle.trim().toLowerCase());
}

// Replace Unicode sub/superscript digits with ASCII equivalents — Helvetica
// doesn't include them and jsPDF falls back to character-by-character positioning.
const SUB_MAP: Record<string, string> = { '₀':'0','₁':'1','₂':'2','₃':'3','₄':'4','₅':'5','₆':'6','₇':'7','₈':'8','₉':'9' };
const SUP_MAP: Record<string, string> = { '⁰':'0','¹':'1','²':'2','³':'3','⁴':'4','⁵':'5','⁶':'6','⁷':'7','⁸':'8','⁹':'9' };

function normalizeForPdf(text: string): string {
  return text
    .replace(/[₀-₉]/g, c => SUB_MAP[c] ?? c)
    .replace(/[⁰¹²³⁴-⁹]/g, c => SUP_MAP[c] ?? c)
    .replace(/→/g, '->')
    .replace(/←/g, '<-')
    .replace(/↑/g, '^')
    .replace(/↓/g, 'v');
}

function runsToText(runs: InlineRun[]): string {
  return runs.map(r => r.text).join('');
}

function addHeaderFooter(doc: jsPDF, az: string, totalPages?: number): void {
  const pageCount = doc.getNumberOfPages();
  const total = totalPages ?? pageCount;
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // Header
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `BAFA Beratungsbericht${az ? ' - ' + az : ''}`,
      MARGIN_LEFT,
      MARGIN_TOP
    );
    doc.setDrawColor(200, 200, 200);
    doc.line(MARGIN_LEFT, MARGIN_TOP + 3, PAGE_WIDTH - MARGIN_RIGHT, MARGIN_TOP + 3);

    // Footer
    doc.line(MARGIN_LEFT, PAGE_HEIGHT - MARGIN_BOTTOM - FOOTER_HEIGHT, PAGE_WIDTH - MARGIN_RIGHT, PAGE_HEIGHT - MARGIN_BOTTOM - FOOTER_HEIGHT);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Seite ${i} von ${total}`,
      PAGE_WIDTH / 2,
      PAGE_HEIGHT - MARGIN_BOTTOM - 2,
      { align: 'center' }
    );
  }
}

function checkPageBreak(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > USABLE_BOTTOM) {
    doc.addPage();
    return USABLE_TOP;
  }
  return y;
}

// ── Silbentrennung für Tabellenzellen ────────────────────────────────────────
// jspdf-autotable bricht zu lange Wörter dort um, wo der Platz endet („Katalogdownloa|d“).
// Stattdessen an Silbengrenzen mit Bindestrich trennen. Muster (de-1996) bewusst statisch
// im Haupt-Bundle: als nachgeladener Chunk fehlten sie offenen Tabs nach jedem Deploy
// (alter Chunk weg → index.html statt JS) und das PDF kam still ohne Trennung.
let silbentrenner: ((wort: string) => string[]) | null =
  (wort) => hyphenateSync(wort, { hyphenChar: '\u00AD', minWordLength: 6 }).split('\u00AD');

/** Früher Lade-Schritt für die nachgeladenen Muster; bleibt für Aufrufer/Tests bestehen. */
export async function ladeSilbentrenner(): Promise<void> {}

/** Trennstellen eines Worts: Silben plus vorhandene Bindestriche („Terrassen-Blitz“). */
function wortTeile(wort: string): string[] {
  const teile: string[] = [];
  for (const stueck of wort.split(/(?<=-)/)) {
    const silben = silbentrenner ? silbentrenner(stueck) : [stueck];
    teile.push(...silben.filter(Boolean));
  }
  return teile;
}

/**
 * Ersatz für doc.splitTextToSize in Tabellenzellen: füllt Zeilen wortweise und trennt ein
 * nicht mehr passendes Wort an der letzten passenden Silbengrenze („Produkt-“ / „informationen“).
 * Passt nicht einmal eine Silbe, bleibt es beim Zeichen-Umbruch von jsPDF.
 */
export function splitMitSilbentrennung(
  doc: jsPDF,
  original: (text: string, max: number, opts?: any) => string[],
  text: string | string[],
  maxBreite: number,
  opts?: { fontSize?: number },
): string[] {
  const fs = opts?.fontSize ?? doc.getFontSize();
  const breite = (s: string) => (doc.getStringUnitWidth(s) * fs) / doc.internal.scaleFactor;
  const absaetze = (Array.isArray(text) ? text : [text]).flatMap(t => String(t).split('\n'));
  const zeilen: string[] = [];
  for (const absatz of absaetze) {
    let zeile = '';
    const woerter = absatz.split(' ');
    for (let i = 0; i < woerter.length; i++) {
      let wort = woerter[i];
      const kandidat = zeile ? `${zeile} ${wort}` : wort;
      if (breite(kandidat) <= maxBreite) { zeile = kandidat; continue; }
      // Wort passt nicht mehr: so viele Silben wie möglich + Bindestrich in diese Zeile.
      for (;;) {
        const teile = wortTeile(wort);
        const prefix = zeile ? `${zeile} ` : '';
        let k = 0;
        for (let j = 1; j < teile.length; j++) {
          const vorne = teile.slice(0, j).join('');
          const strich = vorne.endsWith('-') ? '' : '-';
          if (breite(prefix + vorne + strich) > maxBreite) break;
          // Keine Mini-Reste wie „Handwerksbetrie-be“: je Seite mindestens 3 Zeichen
          // (an vorhandenen Bindestrichen immer erlaubt).
          if (strich && (vorne.length < 3 || teile.slice(j).join('').length < 3)) continue;
          k = j;
        }
        if (k > 0) {
          const vorne = teile.slice(0, k).join('');
          zeilen.push(prefix + vorne + (vorne.endsWith('-') ? '' : '-'));
          wort = teile.slice(k).join('');
          zeile = '';
          if (breite(wort) <= maxBreite) { zeile = wort; break; }
          continue;
        }
        if (zeile) {
          // Keine Silbe passt mehr hinter den Zeileninhalt → Wort beginnt in neuer Zeile.
          zeilen.push(zeile);
          zeile = '';
          if (breite(wort) <= maxBreite) { zeile = wort; break; }
          continue;
        }
        // Nicht einmal die erste Silbe passt in eine leere Zeile → Zeichen-Umbruch.
        const notfall = original.call(doc, wort, maxBreite, opts);
        zeilen.push(...notfall.slice(0, -1));
        zeile = notfall[notfall.length - 1] ?? '';
        break;
      }
    }
    zeilen.push(zeile);
  }
  return zeilen;
}

async function buildPdfBuffer(data: ReportData, esfFileId?: number): Promise<Uint8Array> {
  const { doc, signaturePageIndex } = buildJsPdfDoc(data);

  // Fetch ESF first so we can include its page count in the footer total
  let esfBytes: ArrayBuffer | null = null;
  let esfPageCount = 0;
  if (esfFileId) {
    try {
      const token = localStorage.getItem('auth_token');
      const esfRes = await fetch(`${API_BASE}/api/files/${esfFileId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (esfRes.ok) {
        esfBytes = await esfRes.arrayBuffer();
        const esfDoc = await PDFDocument.load(esfBytes);
        esfPageCount = esfDoc.getPageCount();
      }
    } catch (e) {
      console.warn('ESF-Anhang konnte nicht geladen werden:', e);
    }
  }

  const totalPages = doc.getNumberOfPages() + esfPageCount;
  addHeaderFooter(doc, data.metadata.aktenzeichen, totalPages);

  const rawBytes = new Uint8Array(doc.output('arraybuffer') as ArrayBuffer);
  const reportDoc = await PDFDocument.load(rawBytes);
  reportDoc.setKeywords([`sp:${signaturePageIndex}`]);

  if (esfBytes) {
    try {
      const mergedPdf = await PDFDocument.create();
      const esfDoc = await PDFDocument.load(esfBytes);
      (await mergedPdf.copyPages(reportDoc, reportDoc.getPageIndices())).forEach(p => mergedPdf.addPage(p));
      (await mergedPdf.copyPages(esfDoc, esfDoc.getPageIndices())).forEach(p => mergedPdf.addPage(p));
      mergedPdf.setKeywords([`sp:${signaturePageIndex}`]);
      return mergedPdf.save();
    } catch (e) {
      console.warn('ESF-Anhang konnte nicht gemergt werden:', e);
    }
  }

  return reportDoc.save();
}

export async function getPdfBytes(data: ReportData, esfFileId?: number): Promise<Uint8Array> {
  return buildPdfBuffer(data, esfFileId);
}

function buildJsPdfDoc(data: ReportData): { doc: jsPDF; signaturePageIndex: number } {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let signaturePageIndex = 0;
  let y = USABLE_TOP;

  // === TITLE PAGE ===
  y = 80;
  doc.setFontSize(24);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  const titleLines = doc.splitTextToSize(data.metadata.berichtstitel || 'BAFA Beratungsbericht', CONTENT_WIDTH);
  doc.text(titleLines, PAGE_WIDTH / 2, y, { align: 'center' });
  y += titleLines.length * 10 + 8;

  if (data.company.name) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal');
    doc.text(data.company.name, PAGE_WIDTH / 2, y, { align: 'center' });
    y += 12;
  }

  y += 10;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');

  const metaLines: string[] = [];
  if (data.metadata.aktenzeichen) metaLines.push(`Vorgangsnummer: ${data.metadata.aktenzeichen}`);
  if (data.consultants.length > 0) {
    metaLines.push(`Berater: ${data.consultants.map(c => c.name).join(', ')}`);
  }
  if (data.advisorNumber) {
    metaLines.push(`BAFA-Beraternummer: ${data.advisorNumber}`);
  }
  for (const line of metaLines) { doc.text(line, PAGE_WIDTH / 2, y, { align: 'center' }); y += 6; }

  // === SECTIONS ===
  let isFirstSection = true;
  for (let si = 0; si < data.sections.length; si++) {
    const section = data.sections[si];
    const isLastSection = si === data.sections.length - 1;
    if (isFirstSection) { doc.addPage(); y = USABLE_TOP; isFirstSection = false; }
    // Letzter Abschnitt (Bestätigung + Unterschriften) immer auf eine neue Seite,
    // damit er nicht umbricht und keine Zeile vereinzelt auf die Folgeseite rutscht.
    else if (isLastSection || y > USABLE_BOTTOM - 60) { doc.addPage(); y = USABLE_TOP; }
    else { y += 12; }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    const headingLines = doc.splitTextToSize(section.title, CONTENT_WIDTH);
    y = checkPageBreak(doc, y, headingLines.length * 6 + 4);

    // Track starting page of last section AFTER potential page break
    if (si === data.sections.length - 1) {
      signaturePageIndex = (doc.internal as any).getCurrentPageInfo().pageNumber - 1;
    }
    doc.text(headingLines, MARGIN_LEFT, y);
    // Kein Trennstrich unter der Abschnittsüberschrift; nur kleiner Abstand zum Text
    y += headingLines.length * 6 + 4;

    if (!section.htmlContent || !section.htmlContent.trim()) {
      doc.setFontSize(10); doc.setFont('helvetica', 'italic'); doc.setTextColor(150, 150, 150);
      doc.text('(Kein Inhalt)', MARGIN_LEFT, y); y += 8; continue;
    }

    let blocks = htmlToBlocks(section.htmlContent);
    if (blocks.length > 0 && blocks[0].type === 'heading' && isRedundantHeadingBlock(blocks[0].text, section.title)) {
      blocks = blocks.slice(1);
    }
    y = renderBlocks(doc, blocks, y);
  }

  return { doc, signaturePageIndex };
}

export async function exportPdf(data: ReportData, esfFileId?: number, filename?: string): Promise<void> {
  const az = data.metadata.aktenzeichen ? sanitizeFilename(data.metadata.aktenzeichen) : 'Bericht';
  const today = new Date().toISOString().slice(0, 10);
  // filename (aus user.filenamePattern) hat Vorrang; sonst Standardname
  const finalName = filename?.trim() || `BAFA_Beratungsbericht_${az}_${today}.pdf`;

  const bytes = await buildPdfBuffer(data, esfFileId);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = finalName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function runFontStyle(run: InlineRun): string {
  if (run.bold && run.italic) return 'bolditalic';
  if (run.bold) return 'bold';
  if (run.italic) return 'italic';
  return 'normal';
}

// Detect signature-style paragraphs (e.g. "Ort, Datum    Unterschrift Beraterin")
// that should be split into two columns instead of reflowed as a single line.
function trySignatureColumns(text: string): [string, string] | null {
  // Split on 4+ consecutive spaces or non-breaking spaces (\u00a0)
  const parts = text.split(/[\s\u00a0]{4,}/);
  if (parts.length < 2) return null;
  const left = parts[0].trim();
  const right = parts.slice(1).join(' ').trim();
  if (!left || !right) return null;
  // Only treat as two-column when it matches a signature pattern
  const sigPattern = /^_+$|^Ort[,. ]|^Unterschrift|^Berater|^Antragsteller/;
  if (!sigPattern.test(left) && !sigPattern.test(right)) return null;
  return [left, right];
}

// Render inline runs with mixed bold/italic, with proper word-wrapping and page breaks.
// \n in run text is treated as a forced line break.
function renderMixedInlineRuns(
  doc: jsPDF,
  runs: InlineRun[],
  startX: number,
  startY: number,
  maxWidth: number,
  lineHeight: number,
): number {
  type Token = { text: string; bold: boolean; italic: boolean; forceBreak?: boolean };
  const tokens: Token[] = [];

  for (const run of runs) {
    const bold = !!run.bold;
    const italic = !!run.italic;
    // Split on explicit newlines first to preserve <br> line breaks
    const lines = normalizeForPdf(run.text).split('\n');
    for (let li = 0; li < lines.length; li++) {
      if (li > 0) tokens.push({ text: '', bold, italic, forceBreak: true });
      for (const part of lines[li].split(/(\s+)/)) {
        if (part) tokens.push({ text: part, bold, italic });
      }
    }
  }

  const renderLines: Token[][] = [];
  let currentLine: Token[] = [];
  let currentWidth = 0;

  const flushLine = () => {
    while (currentLine.length > 0 && /^\s+$/.test(currentLine[currentLine.length - 1].text)) currentLine.pop();
    if (currentLine.length > 0) renderLines.push(currentLine);
    currentLine = [];
    currentWidth = 0;
  };

  for (const token of tokens) {
    if (token.forceBreak) { flushLine(); continue; }

    doc.setFont('helvetica', runFontStyle(token));
    const w = doc.getTextWidth(token.text);
    const isSpace = /^\s+$/.test(token.text);

    if (isSpace) {
      if (currentLine.length > 0) { currentLine.push(token); currentWidth += w; }
    } else if (currentWidth + w > maxWidth && currentLine.length > 0) {
      flushLine();
      currentLine = [token];
      currentWidth = w;
    } else {
      currentLine.push(token);
      currentWidth += w;
    }
  }
  flushLine();

  let y = startY;
  for (const line of renderLines) {
    y = checkPageBreak(doc, y, lineHeight);
    let x = startX;
    for (const token of line) {
      doc.setFont('helvetica', runFontStyle(token));
      doc.text(token.text, x, y);
      x += doc.getTextWidth(token.text);
    }
    y += lineHeight;
  }
  return y;
}

function renderBlocks(doc: jsPDF, blocks: Block[], startY: number): number {
  let y = startY;
  let prevWasSignature = false;

  for (const block of blocks) {
    // Unterschriftszeile erkennen (zweispaltig: Unterstriche oder "Ort, Datum / Unterschrift")
    const sigText = block.type === 'paragraph' ? runsToText(block.runs) : '';
    const sigCols = block.type === 'paragraph' ? trySignatureColumns(sigText) : null;
    const isSignature = !!sigCols;
    const isUnderscoreLine = !!sigCols && /^_+$/.test(sigCols[0].trim());
    const isEmptyPara = block.type === 'paragraph' && !sigText.trim();
    if (isSignature && !prevWasSignature) {
      // Übergang in den Unterschriftsblock: mehr Abstand zum Text darüber
      y += 10;
    } else if (isUnderscoreLine && prevWasSignature) {
      // weitere Unterschriftsreihe (z. B. Antragsteller): Platz zur vorherigen Reihe zum Unterschreiben
      y += 12;
    }

    switch (block.type) {
      case 'heading': {
        const fontSize = block.level === 1 ? 13 : block.level === 2 ? 12 : 11;
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        const lines = doc.splitTextToSize(normalizeForPdf(block.text), CONTENT_WIDTH);
        y = checkPageBreak(doc, y, lines.length * 5 + 6);
        y += 3;
        doc.text(lines, MARGIN_LEFT, y);
        y += lines.length * 5 + 3;
        break;
      }

      case 'paragraph': {
        const text = runsToText(block.runs);
        if (!text.trim()) { y += 3; break; }
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);

        // Detect mixed inline formatting (some runs bold, some not)
        const firstBold = !!block.runs[0]?.bold;
        const firstItalic = !!block.runs[0]?.italic;
        const uniformStyle = block.runs.every(r => !!r.bold === firstBold && !!r.italic === firstItalic);
        const hasMixed = !uniformStyle && block.runs.some(r => r.bold || r.italic);

        if (hasMixed) {
          y = renderMixedInlineRuns(doc, block.runs, MARGIN_LEFT, y, CONTENT_WIDTH, 5);
          y += 2;
          break;
        }

        // Signature-style two-column paragraphs (Ort, Datum / Unterschrift)
        const cols = trySignatureColumns(text);
        if (cols) {
          y = checkPageBreak(doc, y, 5 + 2);
          doc.setFont('helvetica', firstBold ? 'bold' : 'normal');
          doc.text(cols[0], MARGIN_LEFT, y);
          doc.text(cols[1], MARGIN_LEFT + CONTENT_WIDTH / 2, y);
          y += 5 + 2;
          break;
        }

        // Uniform style \u2014 render line-by-line to handle multi-page paragraphs
        const style = firstBold && firstItalic ? 'bolditalic' : firstBold ? 'bold' : firstItalic ? 'italic' : 'normal';
        doc.setFont('helvetica', style);
        const lines = doc.splitTextToSize(normalizeForPdf(text), CONTENT_WIDTH);
        for (const line of lines) {
          y = checkPageBreak(doc, y, 5);
          doc.text(line, MARGIN_LEFT, y);
          y += 5;
        }
        y += 2;
        break;
      }

      case 'list': {
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);

        for (let i = 0; i < block.items.length; i++) {
          const bullet = block.ordered ? `${i + 1}. ` : '- ';
          const itemRuns = block.items[i];
          const hasFormatting = itemRuns.some(r => r.bold || r.italic);

          if (hasFormatting) {
            // Prepend bullet as plain run, then render with inline formatting + \n support
            const bulletRun: InlineRun = { text: bullet };
            y = renderMixedInlineRuns(doc, [bulletRun, ...itemRuns], MARGIN_LEFT + 5, y, CONTENT_WIDTH - 8, 5);
          } else {
            doc.setFont('helvetica', 'normal');
            const text = normalizeForPdf(bullet + runsToText(itemRuns));
            const lines = doc.splitTextToSize(text, CONTENT_WIDTH - 8);
            for (const line of lines) {
              y = checkPageBreak(doc, y, 5);
              doc.text(line, MARGIN_LEFT + 5, y);
              y += 5;
            }
          }
          y += 1;
        }
        y += 2;
        break;
      }

      case 'table': {
        if (block.rows.length === 0) break;
        y = checkPageBreak(doc, y, 20);

        // Wie bei Überschrift/Absatz/Liste: Pfeile & Hoch-/Tiefstellungen fehlen in
        // Helvetica — ohne Normalisierung fällt jsPDF auf Buchstabe-für-Buchstabe-
        // Positionierung zurück (sichtbar als auseinandergezogener Text je Zelle).
        const rows = block.rows.map(row => row.map(normalizeForPdf));
        const hasHeader = rows.length > 1;
        const head = hasHeader ? [rows[0]] : undefined;
        const body = hasHeader ? rows.slice(1) : rows;

        // Breite Tabellen (ab 5 Spalten) etwas kleiner — sonst brechen Wörter mitten im
        // Wort um („Ref-erenzpunkt“).
        const breit = Math.max(...rows.map(r => r.length)) >= 5;

        // Für die Dauer der Tabelle trennt splitTextToSize an Silbengrenzen (s. oben).
        const originalSplit = doc.splitTextToSize;
        if (silbentrenner) {
          (doc as any).splitTextToSize = (text: string | string[], max: number, opts?: any) =>
            splitMitSilbentrennung(doc, originalSplit, text, max, opts);
        }
        try {
        autoTable(doc, {
          startY: y,
          head,
          body,
          // Oben/unten wie der Fließtext: ohne diese Ränder läuft eine mehrseitige Tabelle
          // in Fußzeile (Seitenzahl) und auf der Folgeseite in die Kopfzeile.
          margin: { left: MARGIN_LEFT, right: MARGIN_RIGHT, top: USABLE_TOP, bottom: PAGE_HEIGHT - USABLE_BOTTOM },
          // Zeile als Ganzes auf die nächste Seite statt Bruchstücke oben auf der Folgeseite.
          rowPageBreak: 'avoid',
          styles: { fontSize: breit ? 8 : 9, font: 'helvetica', cellPadding: breit ? 1.5 : 2 },
          headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [245, 247, 250] },
        });
        } finally {
          (doc as any).splitTextToSize = originalSplit;
        }

        y = (doc as any).lastAutoTable?.finalY || y + 20;
        y += 4;
        break;
      }
    }

    if (!isEmptyPara) prevWasSignature = isSignature;
  }

  return y;
}
